import { FunctionComponent, useMemo } from 'react';
import styles from './BookingDetails.module.css';
import { DateHighlight, DatesList, AddressOnMap, Directions } from '../../components/index';
import { JoinDirections } from '../JoinDirections';
import { BlockOccurrence, ChildOccurrence, Course, Educator, Occurrence } from '@shared/types';
import { Temporal } from 'temporal-polyfill';

export type BookingDetailProps = {
    course: Course;
    occurrence: Occurrence | (BlockOccurrence & { children: Array<ChildOccurrence> });
    educator_slug: Educator['slug'];
};

export const BookingDetails: FunctionComponent<BookingDetailProps> = ({
    course,
    occurrence,
    educator_slug,
}) => {
    const shownAddress = useMemo(() => {
        if (occurrence.address && Object.values(occurrence.address).filter((x) => x).length) {
            return occurrence.address;
        }
        if (course.type === 'class' || course.type === 'blended') {
            return course.address;
        }
        return undefined;
    }, [course, occurrence]);

    const onlineJoinURL = useMemo(() => {
        if (occurrence.online_join_url) {
            return occurrence.online_join_url;
        }
        if (course.type === 'class' || course.type === 'blended') {
            return course.online_join_url;
        }
        return undefined;
    }, [course, occurrence]);

    const isInPerson = useMemo(() => {
        if (
            (course.type === 'class' || course.type === 'blended') &&
            course.delivery === 'in-person'
        ) {
            return true;
        }
        if (course.type === 'on-request' && occurrence.delivery === 'in-person') {
            return true;
        }
        return false;
    }, [course, occurrence]);

    const isOnline = useMemo(() => {
        if (
            (course.type === 'class' || course.type === 'blended') &&
            course.delivery === 'online'
        ) {
            return true;
        }
        if (course.type === 'on-request' && occurrence.delivery === 'online') {
            return true;
        }
        return false;
    }, [course, occurrence]);

    const childOccurrences = useMemo(() => {
        if ('children' in occurrence) {
            return occurrence.children;
        }
        return null;
    }, [occurrence]);

    const { start, end } = useMemo(() => {
        return {
            start: Temporal.ZonedDateTime.from(occurrence.times.start.zoned_datetime),
            end: Temporal.ZonedDateTime.from(occurrence.times.end.zoned_datetime),
        };
    }, [occurrence]);

    return (
        <div className={styles.lesson_container}>
            <div className={styles.lesson_contents}>
                <h2>Dates &amp; times</h2>
                <div className={styles.all_dates_container}>
                    <div>
                        <h3>From</h3>
                        <div className={styles.single_date_container}>
                            <DateHighlight
                                date={start.toLocaleString(undefined, { day: 'numeric' })}
                                month={start.toLocaleString(undefined, { month: 'short' })}
                            />
                            <div className={styles.dates_details}>
                                <span>{start.toLocaleString(undefined, { weekday: 'long' })}</span>
                                <span>
                                    {start.toLocaleString(undefined, {
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        timeZoneName: 'short',
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h3>To</h3>
                        <div className={styles.single_date_container}>
                            <DateHighlight
                                date={end.toLocaleString(undefined, { day: 'numeric' })}
                                month={end.toLocaleString(undefined, { month: 'short' })}
                            />
                            <div className={styles.dates_details}>
                                <span>{end.toLocaleString(undefined, { weekday: 'long' })}</span>
                                <span>
                                    {end.toLocaleString(undefined, {
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        timeZoneName: 'short',
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {childOccurrences && childOccurrences.length ? (
                    <div>
                        <h2 style={{ marginTop: '40px' }}>Class dates</h2>
                        {/* @ts-expect-error The default value of parent being {} means it expects the type to be {} and not an occurrence */}
                        <DatesList
                            occurrences={childOccurrences}
                            parent={occurrence}
                            activeBooking={true}
                            educator_slug={educator_slug}
                        />
                    </div>
                ) : null}
                {isInPerson && (shownAddress?.street || shownAddress?.postcode) ? (
                    <>
                        <h2 style={{ marginTop: '40px' }}>Directions</h2>
                        <AddressOnMap address={shownAddress} />
                        <Directions address={shownAddress} style={{ margin: '10px 0' }} />
                    </>
                ) : null}
                {isOnline ? (
                    <JoinDirections online_join_url={onlineJoinURL} style={{ marginTop: '40px' }} />
                ) : null}
            </div>
        </div>
    );
};
